import { ref, watch } from 'vue';

/**
 * Composable for swipeable pagination on mobile devices.
 * 
 * @param {import('vue').Ref<number>} currentPage - Ref to the current page number (1-based)
 * @param {import('vue').Ref<number>|import('vue').ComputedRef<number>} totalPages - Ref/computed for total pages
 * @param {Object} [options]
 * @param {number} [options.threshold=40] - Minimum horizontal swipe distance in pixels
 * @param {Function} [options.onPageChange] - Optional callback when page changes
 */
export function useSwipePagination(currentPage, totalPages, options = {}) {
    const threshold = options.threshold || 40;
    const transitionName = ref('swipe-next');
    const bounceClass = ref('');

    let touchStartX = 0;
    let touchStartY = 0;
    let isClickSuppressed = false;
    let bounceTimeout = null;

    const triggerBounce = (name) => {
        bounceClass.value = '';
        clearTimeout(bounceTimeout);
        setTimeout(() => {
            bounceClass.value = name;
            bounceTimeout = setTimeout(() => {
                bounceClass.value = '';
            }, 420);
        }, 10);
    };

    // Track page changes from any source (swipe or pagination controls) to set transition direction
    watch(currentPage, (newVal, oldVal) => {
        if (newVal > oldVal) {
            transitionName.value = 'swipe-next';
        } else if (newVal < oldVal) {
            transitionName.value = 'swipe-prev';
        }
    });

    const onTouchStart = (e) => {
        if (!e.touches || e.touches.length !== 1) return;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
    };

    const onTouchEnd = (e) => {
        if (!e.changedTouches || e.changedTouches.length !== 1) return;
        const dx = e.changedTouches[0].clientX - touchStartX;
        const dy = e.changedTouches[0].clientY - touchStartY;

        // Verify horizontal dominance and threshold to avoid interfering with vertical scrolling
        if (Math.abs(dx) >= threshold && Math.abs(dx) > Math.abs(dy) * 1.2) {
            if (dx < 0) {
                // Swipe Left -> Next Page
                if (currentPage.value < totalPages.value) {
                    transitionName.value = 'swipe-next';
                    currentPage.value++;
                    suppressClicksBriefly();
                    if (options.onPageChange) options.onPageChange(currentPage.value);
                } else {
                    // Already on last page -> trigger end bounce
                    triggerBounce('bounce-end-left');
                    suppressClicksBriefly();
                }
            } else if (dx > 0) {
                // Swipe Right -> Previous Page
                if (currentPage.value > 1) {
                    transitionName.value = 'swipe-prev';
                    currentPage.value--;
                    suppressClicksBriefly();
                    if (options.onPageChange) options.onPageChange(currentPage.value);
                } else {
                    // Already on first page -> trigger start bounce
                    triggerBounce('bounce-end-right');
                    suppressClicksBriefly();
                }
            }
        }
    };

    const suppressClicksBriefly = () => {
        isClickSuppressed = true;
        setTimeout(() => {
            isClickSuppressed = false;
        }, 300);
    };

    const onClickCapture = (e) => {
        if (isClickSuppressed) {
            e.stopPropagation();
            e.preventDefault();
        }
    };

    return {
        transitionName,
        bounceClass,
        onTouchStart,
        onTouchEnd,
        onClickCapture
    };
}

/**
 * Generic touch swipe listener for custom left/right actions (e.g., Lightbox navigation).
 * 
 * @param {Object} actions
 * @param {Function} [actions.onSwipeLeft] - Called on swipe left (e.g., next)
 * @param {Function} [actions.onSwipeRight] - Called on swipe right (e.g., prev)
 * @param {number} [actions.threshold=40] - Minimum horizontal swipe distance
 */
export function useSwipeGesture(actions = {}) {
    const threshold = actions.threshold || 40;
    let touchStartX = 0;
    let touchStartY = 0;
    let isClickSuppressed = false;

    const onTouchStart = (e) => {
        if (!e.touches || e.touches.length !== 1) return;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
    };

    const onTouchEnd = (e) => {
        if (!e.changedTouches || e.changedTouches.length !== 1) return;
        const dx = e.changedTouches[0].clientX - touchStartX;
        const dy = e.changedTouches[0].clientY - touchStartY;

        if (Math.abs(dx) >= threshold && Math.abs(dx) > Math.abs(dy) * 1.2) {
            if (dx < 0 && typeof actions.onSwipeLeft === 'function') {
                actions.onSwipeLeft();
                suppressClicksBriefly();
            } else if (dx > 0 && typeof actions.onSwipeRight === 'function') {
                actions.onSwipeRight();
                suppressClicksBriefly();
            }
        }
    };

    const suppressClicksBriefly = () => {
        isClickSuppressed = true;
        setTimeout(() => {
            isClickSuppressed = false;
        }, 300);
    };

    const onClickCapture = (e) => {
        if (isClickSuppressed) {
            e.stopPropagation();
            e.preventDefault();
        }
    };

    return {
        onTouchStart,
        onTouchEnd,
        onClickCapture
    };
}
